import { useEffect } from "react";

const COMPANY = "Ask Top Foster — ATF WebWorks";
const CONTACT_EMAIL = "support@asktopfoster.com";
const EFFECTIVE = "July 2, 2026";

function LegalLayout({ title, testid, children }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Legal</div>
      <h1 className="font-heading text-4xl text-white mb-3">{title}</h1>
      <p className="text-gray-500 text-sm mb-12">Effective {EFFECTIVE} · {COMPANY}</p>
      <div className="space-y-8 text-gray-300 leading-relaxed">{children}</div>
    </div>
  );
}

const H = ({ children }) => <h2 className="font-heading text-xl text-white mb-3">{children}</h2>;
const P = ({ children }) => <p className="text-gray-400">{children}</p>;
const Block = ({ title, children }) => <section><H>{title}</H>{children}</section>;

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" testid="legal-privacy">
      <P>This Privacy Policy explains how {COMPANY} ("we", "us") collects, uses, and protects your information when you use our website, marketplace, and client services.</P>
      <Block title="Information we collect">
        <P>We collect information you provide directly — such as your name, email, phone, organization details, and project requirements submitted through our questionnaire, account registration, or messages. We also collect payment-related information processed securely by our payment provider (Stripe), and basic technical data such as IP address and usage analytics.</P>
      </Block>
      <Block title="How we use your information">
        <P>We use your information to deliver our services, prepare proposals, process payments, provide support, send transactional and service-related emails, and improve our offerings. We do not sell your personal information.</P>
      </Block>
      <Block title="Payment processing">
        <P>Payments are processed by Stripe. We do not store full card numbers on our servers. Stripe's handling of your data is governed by Stripe's own privacy policy.</P>
      </Block>
      <Block title="Cookies & analytics">
        <P>We use cookies and analytics tools to understand how visitors use our site and to keep you signed in. You can control cookies through your browser settings.</P>
      </Block>
      <Block title="Data retention & security">
        <P>We retain your data for as long as needed to provide services and meet legal obligations, and we apply reasonable safeguards to protect it. No method of transmission is 100% secure.</P>
      </Block>
      <Block title="Your rights">
        <P>You may request access to, correction of, or deletion of your personal information by contacting us at {CONTACT_EMAIL}.</P>
      </Block>
      <Block title="Contact">
        <P>Questions about this policy? Email us at <a className="text-gold hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</P>
      </Block>
    </LegalLayout>
  );
}

export function Terms() {
  return (
    <LegalLayout title="Terms of Service" testid="legal-terms">
      <P>These Terms of Service govern your use of the {COMPANY} website, marketplace, and services. By using our site or purchasing from us, you agree to these terms.</P>
      <Block title="Services">
        <P>We provide website design services, custom builds, and downloadable website templates. Custom projects are scoped via proposals; deliverables, timelines, and pricing are defined per engagement.</P>
      </Block>
      <Block title="Accounts">
        <P>You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account.</P>
      </Block>
      <Block title="Purchases & licenses">
        <P>Template purchases grant you a non-exclusive license to use the template for a single end product, per the license terms provided at purchase. Redistribution or resale of templates is prohibited.</P>
      </Block>
      <Block title="Payments & deposits">
        <P>Custom projects typically require a deposit to begin, with the balance due at agreed milestones or launch. All fees are stated in US dollars unless noted otherwise.</P>
      </Block>
      <Block title="Intellectual property">
        <P>Upon full payment, ownership of the final custom deliverables transfers to you, except for third-party assets and our pre-existing tools/components, which remain licensed to you for use in the delivered product.</P>
      </Block>
      <Block title="AI assistant (Top Foster)">
        <P>"Top Foster" is an automated AI guide and assistant provided within our services to help you complete assessments and explore recommendations. It is not a real person and is not the individual Top Foster. AI-generated guidance, plans, and recommendations are provided for informational purposes, may contain errors, and do not constitute professional, legal, or financial advice.</P>
      </Block>
      <Block title="Acceptable use">
        <P>You agree not to misuse the services, attempt unauthorized access, or use our work for unlawful purposes.</P>
      </Block>
      <Block title="Limitation of liability">
        <P>Services are provided "as is." To the maximum extent permitted by law, we are not liable for indirect or consequential damages arising from use of our services.</P>
      </Block>
      <Block title="Contact">
        <P>Questions about these terms? Email us at <a className="text-gold hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</P>
      </Block>
    </LegalLayout>
  );
}

export function Refund() {
  return (
    <LegalLayout title="Refund & Delivery Policy" testid="legal-refund">
      <P>This policy describes how digital products and services are delivered and our approach to refunds.</P>
      <Block title="Delivery of digital templates">
        <P>Website templates are delivered digitally. After a successful purchase, your download and license key become available immediately in your Client Portal Download Center. No physical items are shipped.</P>
      </Block>
      <Block title="Refunds on templates">
        <P>Because templates are digital goods delivered instantly, all sales are generally final. If a template is materially defective or not as described, contact us within 14 days of purchase at {CONTACT_EMAIL} and we will work to resolve the issue, which may include a fix or a refund at our discretion.</P>
      </Block>
      <Block title="Custom design services & deposits">
        <P>Deposits reserve your project slot and cover initial discovery and design work. Deposits are non-refundable once work has begun. Milestone and balance payments are non-refundable for work already completed. If you wish to cancel, contact us and we will confirm the status of work and any remaining balance.</P>
      </Block>
      <Block title="Chargebacks">
        <P>Please contact us before initiating a chargeback so we can resolve any concern directly and fairly.</P>
      </Block>
      <Block title="Contact">
        <P>For any delivery or refund question, email <a className="text-gold hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</P>
      </Block>
    </LegalLayout>
  );
}
